import { useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  api,
  type ForwardGates,
  type HealthCheck,
  type PortfolioSummary,
  type PortfoliosResponse,
  type SelfTraderStatus,
} from "@/lib/api";
import { queryKeys } from "@/lib/queryClient";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { MoneyDelta } from "@/components/MoneyDelta";
import { formatDateTime, formatMoney, formatPrice } from "@/lib/format";
import { pushToast } from "@/lib/toast";
import { cn } from "@/lib/utils";
import { isMultiDay, SELF_TRADER_PROBE, timeframeLabel } from "@/lib/selfTrader";

/**
 * M41 — the self-trader, visible at last.
 *
 * Until now the bot's only faces were the health strip, the system channel and raw JSON. This
 * page is its own book, deliberately apart from Trades: nothing here is a Telegram signal, and
 * nothing here is real money. In `observe` mode nothing reaches the broker at all; every
 * figure is a paper portfolio on live prices.
 *
 * The one question the page exists to answer is "how close is any strategy to trading?", so
 * every gate the promoter checks is shown per portfolio, with the thresholds SERVED by the
 * route (`gates`) rather than typed in here — the page's "of 56 days" is the promoter's own.
 *
 * Polled at 30 s because that is the module's own poll; asking faster shows nothing new.
 */

const DAY = 86_400_000;
const POLL_MS = 30_000;

/** The module answers 503 while it is off or still connecting; that is a state, not an error. */
export function isUnavailable(error: unknown): boolean {
  return (error as { status?: number } | null)?.status === 503;
}

/** What the self-trader probe carries in `meta` (apps/server/src/modules/selfTrader/health.ts). */
type ProbeMeta = { accountId?: number | string; streamingTicks?: boolean; mode?: string };

export function SelfTrader() {
  const status = useQuery({ queryKey: queryKeys.selfTraderStatus, queryFn: api.selfTrader.status, refetchInterval: POLL_MS });
  const portfolios = useQuery({
    queryKey: queryKeys.selfTraderPortfolios,
    queryFn: api.selfTrader.portfolios,
    refetchInterval: POLL_MS,
  });
  const health = useQuery({ queryKey: queryKeys.health, queryFn: api.health, refetchInterval: POLL_MS });
  const probe = health.data?.checks[SELF_TRADER_PROBE];

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-lg font-semibold">Self-trader</h1>
        <p className="text-xs text-muted-foreground">
          The bot's own paper portfolios. Not Telegram signals, and not the live account.
        </p>
      </div>

      {/* Error before loading, as on Reports: a failed query never delivers data. */}
      {status.isError ? (
        isUnavailable(status.error) ? (
          <NotRunning probe={probe} />
        ) : (
          <p className="text-sm text-destructive">Could not load the self-trader's status.</p>
        )
      ) : status.isLoading || !status.data ? (
        <Skeleton className="h-32 w-full" />
      ) : (
        <>
          <HeaderCard status={status.data} probe={probe} data={portfolios.data} />
          <ControlsCard status={status.data} />
        </>
      )}

      {portfolios.isError ? (
        isUnavailable(portfolios.error) ? null : (
          <p className="text-sm text-destructive">Could not load the portfolios.</p>
        )
      ) : portfolios.isLoading || !portfolios.data ? (
        <Skeleton className="h-64 w-full" />
      ) : (
        <>
          <GateExplainer data={portfolios.data} />
          <PortfolioList data={portfolios.data} />
        </>
      )}
    </div>
  );
}

function NotRunning({ probe }: { probe: HealthCheck | undefined }) {
  return (
    <Card>
      <CardContent className="space-y-1 p-4 text-sm">
        <p className="font-medium">The self-trader is not running.</p>
        <p className="text-muted-foreground">
          {probe?.state === "disabled"
            ? "Its module is switched off on this server."
            : probe?.detail
              ? `It is starting, or waiting on the broker: ${probe.detail}.`
              : "It is starting, or waiting on the broker. This page refreshes on its own."}
        </p>
      </CardContent>
    </Card>
  );
}

/** The earliest forward start across portfolios, or null when none has seen a quote yet. */
export function observationStart(portfolios: readonly PortfolioSummary[]): number | null {
  const starts = portfolios.map((p) => p.forward.startedAt).filter((t): t is number => t !== null);
  return starts.length === 0 ? null : Math.min(...starts);
}

const shortDate = (ms: number) =>
  new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short", year: "numeric" }).format(new Date(ms));

function HeaderCard({
  status,
  probe,
  data,
}: {
  status: SelfTraderStatus;
  probe: HealthCheck | undefined;
  data: PortfoliosResponse | undefined;
}) {
  const meta = (probe?.meta ?? {}) as ProbeMeta;
  const { control } = status;
  const start = data ? observationStart(data.portfolios) : null;
  const now = Date.now();

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0">
        <CardTitle>{!status.running ? "Stopped" : control.halt ? "Halted" : control.paused ? "Entries paused" : "Running"}</CardTitle>
        <Badge variant={status.mode === "observe" ? "secondary" : "default"} className="uppercase">
          {status.mode}
        </Badge>
      </CardHeader>
      <CardContent className="space-y-2 text-sm">
        {control.halt && <p className="text-destructive">Halt: {control.halt}</p>}
        {control.paused && <p className="text-yellow-500">Paused: {control.paused}</p>}
        <p className="text-muted-foreground">
          {status.mode === "observe"
            ? "Observe mode. Every strategy paper-trades on live prices, and nothing is sent to the broker, even for a strategy that qualifies."
            : status.mode === "shadow"
              ? "Shadow mode. Every strategy paper-trades on live prices, and every paper position is also opened and closed as a $1,050 order on the eToro demo account, to measure what execution costs against paper."
              : "Demo mode. A promoted strategy places 0.01-lot orders on the demo account."}
        </p>
        <div className="grid grid-cols-2 gap-2 text-xs">
          <Field label="Account" value={meta.accountId === undefined ? "—" : typeof meta.accountId === "number" ? `#${meta.accountId}` : meta.accountId} />
          <Field
            label="Live ticks"
            value={meta.streamingTicks === undefined ? "—" : meta.streamingTicks ? "streaming" : "not streaming"}
          />
          <Field label="Health" value={probe ? `${probe.state}${probe.detail ? ` · ${probe.detail}` : ""}` : "—"} />
          <Field label="Active strategy" value={status.active.length === 0 ? "none" : status.active.join(", ")} />
        </div>
        {data && (
          <p className="text-xs text-muted-foreground">
            {start === null ? (
              "No portfolio has seen a quote yet, so the forward clock has not started."
            ) : (
              <>
                Observing since {shortDate(start)}: day {Math.floor((now - start) / DAY)} of {data.gates.days}. The
                earliest any strategy could qualify is {shortDate(start + data.gates.days * DAY)}.
              </>
            )}
          </p>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Pause, resume, reset a halt and flatten, each behind a dialog. The module rejects an empty
 * reason, so the dialog asks for one; its refusals (409) are shown in its own words.
 *
 * Flatten appears only when the bot has orders at the broker, in demo or shadow mode: in observe
 * there is nothing to close, and a button that does nothing teaches you to ignore buttons.
 */
function ControlsCard({ status }: { status: SelfTraderStatus }) {
  const { control } = status;
  return (
    <div className="flex flex-wrap gap-2">
      {control.paused ? (
        <ControlDialog
          label="Resume entries"
          title="Resume new entries?"
          description="The bot starts opening paper positions again on its next poll."
          run={() => api.selfTrader.resume()}
          done="Entries resumed"
        />
      ) : (
        <ControlDialog
          label="Pause entries"
          title="Pause new entries?"
          description="Open positions keep being managed; nothing new is opened until you resume."
          needsReason
          run={(reason) => api.selfTrader.pause(reason)}
          done="Entries paused"
        />
      )}
      {control.halt && (
        <ControlDialog
          label="Reset halt"
          title="Reset the halt?"
          description={`The bot halted itself: ${control.halt}. Resetting says you have looked at why.`}
          needsReason
          run={(reason) => api.selfTrader.resetHalt(reason)}
          done="Halt reset"
        />
      )}
      {status.mode !== "observe" && (
        <ControlDialog
          label="Flatten"
          title="Close everything the bot holds?"
          description="Every position the self-trader opened on the demo account is closed at market."
          needsReason
          destructive
          run={(reason) => api.selfTrader.flatten(reason)}
          done="Flatten sent"
        />
      )}
    </div>
  );
}

function ControlDialog({
  label,
  title,
  description,
  needsReason = false,
  destructive = false,
  run,
  done,
}: {
  label: string;
  title: string;
  description: string;
  needsReason?: boolean;
  destructive?: boolean;
  run: (reason: string) => Promise<unknown>;
  done: string;
}) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const action = useMutation({
    mutationFn: () => run(reason.trim()),
    onSuccess: () => {
      setOpen(false);
      setReason("");
      void queryClient.invalidateQueries({ queryKey: ["selfTrader"] });
      void queryClient.invalidateQueries({ queryKey: queryKeys.health });
      pushToast(done);
    },
  });
  const ready = !needsReason || reason.trim().length > 0;

  return (
    <>
      <Button size="sm" variant={destructive ? "destructive" : "outline"} onClick={() => setOpen(true)}>
        {label}
      </Button>
      <Dialog
        open={open}
        onOpenChange={(next) => {
          setOpen(next);
          if (!next) action.reset();
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
            <DialogDescription>{description}</DialogDescription>
          </DialogHeader>
          {needsReason && (
            <Input
              aria-label="Reason"
              placeholder="Why? (kept in the bot's log)"
              value={reason}
              maxLength={500}
              onChange={(e) => setReason(e.target.value)}
            />
          )}
          {action.isError && <p className="text-sm text-destructive">{(action.error as Error).message}</p>}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={action.isPending}>
              Cancel
            </Button>
            <Button
              variant={destructive ? "destructive" : "default"}
              onClick={() => action.mutate()}
              disabled={action.isPending || !ready}
            >
              {action.isPending ? "Working…" : label}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

/**
 * One line on what it takes, so a page full of green money never reads as "about to trade".
 * Research evidence comes from a historical-data study that is not connected yet (phase 2);
 * while no portfolio has any, that is said outright, because it blocks every promotion.
 */
function GateExplainer({ data }: { data: PortfoliosResponse }) {
  const { gates } = data;
  const researchConnected = data.portfolios.some((p) => p.forward.researchEvidence);
  return (
    <div className="space-y-1 rounded-lg border border-border bg-card px-3 py-2 text-xs text-muted-foreground">
      <p>
        A strategy is promoted only after {gates.days} days of paper trading, {gates.observedDays} market days,{" "}
        {gates.closes} paper closes with a positive net after costs, and a historical research study that selected it.
      </p>
      {!researchConnected && (
        <p className="text-foreground">
          Research is not connected yet (phase 2). Until it is, no strategy can be promoted, however well it
          paper-trades.
        </p>
      )}
    </div>
  );
}

/** How far through the three counted gates, 0–1, each capped so one cannot carry the others. */
export function progressOf(p: PortfolioSummary, gates: ForwardGates): number {
  const f = p.forward;
  return (
    (Math.min(1, f.days / gates.days) + Math.min(1, f.observedDays / gates.observedDays) + Math.min(1, f.closes / gates.closes)) /
    3
  );
}

export function sortPortfolios(
  list: readonly PortfolioSummary[],
  by: "net" | "progress",
  gates: ForwardGates,
): PortfolioSummary[] {
  const key = (p: PortfolioSummary) => (by === "net" ? p.net : progressOf(p, gates));
  return [...list].sort((a, b) => key(b) - key(a) || a.strategyId.localeCompare(b.strategyId));
}

function Chip({ active, onClick, children }: { active: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        "rounded-full border px-2.5 py-1 text-xs transition-colors",
        active ? "border-primary bg-primary/10 text-foreground" : "border-border text-muted-foreground",
      )}
    >
      {children}
    </button>
  );
}

function PortfolioList({ data }: { data: PortfoliosResponse }) {
  const [sortBy, setSortBy] = useState<"net" | "progress">("net");
  const [symbol, setSymbol] = useState<string | null>(null);
  const symbols = [...new Set(data.portfolios.map((p) => p.symbol))].sort();
  const shown = sortPortfolios(
    data.portfolios.filter((p) => symbol === null || p.symbol === symbol),
    sortBy,
    data.gates,
  );
  const traded = data.portfolios.filter((p) => p.closedTrades > 0).length;
  const researchConnected = data.portfolios.some((p) => p.forward.researchEvidence);

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-1.5">
        <Chip active={sortBy === "net"} onClick={() => setSortBy("net")}>
          By net
        </Chip>
        <Chip active={sortBy === "progress"} onClick={() => setSortBy("progress")}>
          By progress
        </Chip>
        <span className="mx-1 h-4 w-px bg-border" />
        <Chip active={symbol === null} onClick={() => setSymbol(null)}>
          All
        </Chip>
        {symbols.map((s) => (
          <Chip key={s} active={symbol === s} onClick={() => setSymbol(s)}>
            {s}
          </Chip>
        ))}
      </div>
      <p className="text-xs text-muted-foreground">
        {data.portfolios.length} portfolios, {traded} of {data.portfolios.length} with a closed trade.
      </p>
      {shown.length === 0 ? (
        <p className="text-sm text-muted-foreground">No portfolios yet.</p>
      ) : (
        shown.map((p) => (
          <PortfolioCard
            key={p.hash}
            portfolio={p}
            gates={data.gates}
            currency={data.currency}
            researchConnected={researchConnected}
          />
        ))
      )}
    </div>
  );
}

const iso = (ms: number) => new Date(ms).toISOString();

/**
 * The reasons a record is permanently disqualified. Each flag is only ever set, never cleared,
 * by the module (portfolio.ts), so the wording says "can no longer" rather than "not yet".
 */
export function blockers(p: PortfolioSummary): string[] {
  const out: string[] = [];
  if (p.halted) out.push("Halted: its drawdown reached the limit. This record can no longer qualify.");
  if (!p.forward.costsComplete)
    out.push("Costs incomplete: a fill had no priced commission, swap or session. This record can no longer qualify.");
  if (!p.forward.dataComplete)
    out.push("Data gap: prices went missing while a position was open. This record can no longer qualify.");
  // A multi-day strategy is unpromotable by design, which is not a fault; its card says so in grey.
  if (!p.forward.promotable && !isMultiDay(p)) out.push("Not promotable: the promoter only considers fixed-size strategies without a model.");
  return out;
}

type GateState = "met" | "pending" | "blocked";

export function gateChips(
  p: PortfolioSummary,
  gates: ForwardGates,
  currency: string,
  researchConnected: boolean,
): { key: string; label: string; state: GateState }[] {
  const f = p.forward;
  const started = f.startedAt !== null;
  const quality: { key: string; label: string; state: GateState }[] = [
    { key: "costs", label: f.costsComplete ? "costs" : "costs incomplete", state: f.costsComplete ? "met" : "blocked" },
    { key: "data", label: f.dataComplete ? "data" : "data gap", state: f.dataComplete ? "met" : "blocked" },
    { key: "fresh", label: f.fresh ? "live" : "no recent quote", state: f.fresh ? "met" : "pending" },
  ];
  // M49 — no promotion gate applies to a multi-day strategy, only whether its record is sound.
  if (isMultiDay(p)) return quality;
  return [
    {
      key: "days",
      label: started ? `${Math.floor(f.days)}/${gates.days} days` : "not started",
      state: f.days >= gates.days ? "met" : "pending",
    },
    {
      key: "observed",
      label: `${f.observedDays}/${gates.observedDays} market days`,
      state: f.observedDays >= gates.observedDays ? "met" : "pending",
    },
    { key: "closes", label: `${f.closes}/${gates.closes} closes`, state: f.closes >= gates.closes ? "met" : "pending" },
    {
      key: "net",
      // The promoter's net, not the card's headline: every service cost since the start counts.
      label: !started ? "net —" : `net ${formatMoney(f.net, currency)}`,
      state: f.net > 0 && f.equityAboveCapital && f.closes >= gates.closes && f.last50Net > 0 ? "met" : "pending",
    },
    ...quality,
    {
      key: "research",
      label: f.researchEvidence ? "research" : researchConnected ? "no research" : "research not connected",
      state: f.researchEvidence ? "met" : "pending",
    },
  ];
}

function GateChip({ label, state }: { label: string; state: GateState }) {
  return (
    <span
      data-state={state}
      className={cn(
        "rounded-full border px-2 py-0.5 text-[11px] tabular-nums",
        state === "met" && "border-success/40 bg-success/10 text-success",
        state === "pending" && "border-border text-muted-foreground",
        state === "blocked" && "border-destructive/40 bg-destructive/10 text-destructive",
      )}
    >
      {label}
    </span>
  );
}

function PortfolioCard({
  portfolio: p,
  gates,
  currency,
  researchConnected,
}: {
  portfolio: PortfolioSummary;
  gates: ForwardGates;
  currency: string;
  researchConnected: boolean;
}) {
  const reasons = blockers(p);
  return (
    <Card data-testid={`portfolio-${p.strategyId}`}>
      <CardContent className="space-y-2 p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="truncate text-sm font-medium">
              <span className="font-mono">{p.symbol}</span> · {timeframeLabel(p.timeframe)} · {p.family}
            </div>
            <div className="text-xs text-muted-foreground">
              {p.closedTrades === 0
                ? "No closed trades yet"
                : `${p.closedTrades} closed · won ${Math.round((p.winRate ?? 0) * 100)}% of ${p.closedTrades} · PF ${
                    p.profitFactor === null ? "—" : p.profitFactor.toFixed(2)
                  } · ${p.pips >= 0 ? "+" : ""}${p.pips.toFixed(1)} pips`}
            </div>
          </div>
          <div className="flex shrink-0 flex-col items-end">
            {/* The module's currency, not the live account's: the two need not be the same. */}
            <MoneyDelta value={p.net} currency={currency} />
            <span className="text-xs text-muted-foreground">
              max drawdown {(p.maxDrawdown * 100).toFixed(1)}%
            </span>
          </div>
        </div>

        {p.open && (
          <p className="text-xs">
            <span className={p.open.side === "sell" ? "text-destructive" : "text-success"}>
              {p.open.side.toUpperCase()}
            </span>{" "}
            open at <span className="font-mono">{formatPrice(p.open.entry)}</span> · SL{" "}
            <span className="font-mono">{formatPrice(p.open.stop)}</span> · TP{" "}
            <span className="font-mono">{formatPrice(p.open.target)}</span> · since {formatDateTime(iso(p.open.openedAt))}
          </p>
        )}
        {p.pending && (
          <p className="text-xs text-muted-foreground">
            {isMultiDay(p)
              ? `${p.pending.side.toUpperCase()} entry waiting for the next US open`
              : `${p.pending.side.toUpperCase()} entry waiting for a quote, until ${formatDateTime(iso(p.pending.expiresAt))}`}
          </p>
        )}
        {isMultiDay(p) && (
          <p className="text-xs text-muted-foreground">
            Multi-day: daily bars, long only, held overnight until its stop, target or time limit. Measured on paper and
            the demo account; not promoted, because the backtest cannot replay multi-day trades yet.
          </p>
        )}

        {reasons.map((reason) => (
          <p key={reason} className="rounded-md bg-destructive/10 px-2 py-1 text-xs text-destructive">
            {reason}
          </p>
        ))}

        <div className="flex flex-wrap gap-1">
          {gateChips(p, gates, currency, researchConnected).map((chip) => (
            <GateChip key={chip.key} label={chip.label} state={chip.state} />
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0">
      <span className="text-muted-foreground">{label}: </span>
      <span className="break-words text-foreground">{value}</span>
    </div>
  );
}

